import { useState, useMemo, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/modern/components/ui/dialog-shadcn";
import { Button } from "@/modern/components/ui/button-shadcn";
import { useSMMGeneratorContext } from "../context/SMMGeneratorContext";
import { useManifestMarketingTags } from "../utils/useManifestMarketingTags";
import type { ManifestTagView } from "../utils/useManifestMarketingTags";
import { TagReviewListPanel, type TagReviewFilter } from "./TagReviewListPanel";
import { TagReviewDetailPanel } from "./TagReviewDetailPanel";

export const TagReviewDialog = () => {
  const { tagReviewOpen, setTagReviewOpen } = useSMMGeneratorContext();
  const { tags, mappings } = useManifestMarketingTags();

  const [filter, setFilter] = useState<TagReviewFilter>("all");
  const [selectedGlobalIndex, setSelectedGlobalIndex] = useState<number | null>(null);

  const filteredIndices = useMemo(() => {
    return tags
      .map((t, i) => ({ t, i }))
      .filter(({ t }) => filter === "all" || t.type === filter)
      .map(({ i }) => i);
  }, [tags, filter]);

  useEffect(() => {
    if (selectedGlobalIndex == null) return;
    if (selectedGlobalIndex >= tags.length) {
      setSelectedGlobalIndex(null);
      return;
    }
    if (!filteredIndices.includes(selectedGlobalIndex)) {
      setSelectedGlobalIndex(null);
    }
  }, [filteredIndices, selectedGlobalIndex, tags.length]);

  useEffect(() => {
    if (!tagReviewOpen) {
      setFilter("all");
      setSelectedGlobalIndex(null);
    }
  }, [tagReviewOpen]);

  const selectedTag: ManifestTagView | null =
    selectedGlobalIndex != null ? tags[selectedGlobalIndex] ?? null : null;

  const linkedProblemLabel =
    selectedTag?.type === "solution"
      ? mappings.find((m) => m.solution_label === selectedTag.label)?.problem_label ?? null
      : null;

  return (
    <Dialog open={tagReviewOpen} onOpenChange={setTagReviewOpen}>
      <DialogContent
        showCloseButton
        className="w-full max-w-4xl h-[min(620px,90dvh)] p-0 gap-0 !flex flex-col overflow-hidden border-border sm:max-w-4xl"
      >
        <DialogHeader className="px-5 py-4 shrink-0 border-b border-border space-y-0.5 pr-14 text-left">
          <DialogTitle className="text-base font-semibold">Marketing Tags</DialogTitle>
          <DialogDescription className="text-xs">
            Tags generated from your active Context. They update automatically when you activate a new Context.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-1 min-h-0 overflow-hidden">
          {tags.length === 0 ? (
            <div className="flex flex-1 items-center justify-center px-6 text-center">
              <p className="text-sm text-muted-foreground max-w-sm leading-relaxed">
                No tags yet. Activate a completed Context to generate your marketing tags automatically.
              </p>
            </div>
          ) : (
            <>
              <TagReviewListPanel
                tags={tags}
                filteredIndices={filteredIndices}
                filter={filter}
                onFilterChange={setFilter}
                selectedGlobalIndex={selectedGlobalIndex}
                onSelect={setSelectedGlobalIndex}
              />
              <TagReviewDetailPanel
                tag={selectedTag}
                linkedProblemLabel={linkedProblemLabel}
              />
            </>
          )}
        </div>

        <DialogFooter className="px-5 py-3 shrink-0 border-t border-border flex flex-row items-center justify-between gap-3 sm:justify-between">
          <span className="text-xs text-muted-foreground tabular-nums">
            {tags.length === 0
              ? "0 tags"
              : `Showing ${filteredIndices.length} of ${tags.length}`}
          </span>
          <Button variant="outline" onClick={() => setTagReviewOpen(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
